import { fetchWithRetry as fetch, responseError } from './apiError';
import axiosInstance from './axiosInstance';
import { eventService } from './eventService';
import type { Task } from './taskService';
import { tokenService } from './tokenService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface BuildPhase {
  id: string;
  project_id: string;
  phase_number: number;
  name: string;
  goal: string | null;
  description: string | null;
  status: 'planned' | 'active' | 'completed' | 'archived';
  deliverables: string[];
  personas: string[];
  pages: string[];
  task_ids: string[];
  task_count: number;
  completed_task_count: number;
  completion_percentage: number;
  role_counts: Record<string, number>;
  base_credits: number;
  estimated_credits: number;
  completed_credits: number;
  remaining_credits: number;
  created_at: string;
  updated_at: string;
}

export interface PhaseBuildTaskOption {
  id: string;
  task_number: number | null;
  title: string;
  summary: string;
  role: string | null;
  status: string;
  estimated_credits: number;
  dependency_ids: string[];
  selected_by_default: boolean;
  /** False when the task is visible for phase context but cannot join this run. */
  selectable?: boolean;
}

export interface MoveTaskToPhaseResponse {
  success: boolean;
  task?: Task;
  phases: BuildPhase[];
}

export type PhaseBuildBlockingReason =
  | 'assignment_required'
  | 'build_in_progress'
  | 'dependencies'
  | 'needs_attention'
  | 'insufficient_credits'
  | 'all_completed'
  /** No build task exists yet: architecture and the task plan come first. */
  | 'task_plan_missing';

export interface PhaseBuildExecutionState {
  current_phase: BuildPhase | null;
  active_phase_id: string | null;
  required_credits: number;
  available_credits: number;
  is_unlimited?: boolean;
  credit_shortfall: number;
  affordable_credits?: number;
  affordable_task_count?: number;
  deferred_task_count?: number;
  can_start: boolean;
  blocking_reason: PhaseBuildBlockingReason | null;
  blocking_task_count: number;
  unassigned_task_count: number;
  all_completed: boolean;
  /** Exact current-phase tasks the server can queue in the next build. */
  runnable_tasks: PhaseBuildTaskOption[];
  /** Every task in the current phase, including blocked and completed work. */
  phase_tasks?: PhaseBuildTaskOption[];
}

/** One earlier step a build waits for (GET …/build-phases/readiness), with the control that does it. */
export interface BuildPrerequisite {
  key: 'pages' | 'architecture' | 'task_plan' | string;
  summary: string;
  /** add_credits: the shortfall; continue: press the gate button named in ``labels``; wait: already running. */
  action: 'add_credits' | 'continue' | 'wait' | string;
  label: string | null;
  credits: number | null;
  labels: string[];
}

export interface BuildReadiness {
  can_build: boolean;
  prerequisites: BuildPrerequisite[];
}

export interface PhaseTaskSelectionSummary {
  id: string;
  project_id: string;
  phase_id: string;
  source_entry_key: string;
  selected_task_ids: string[];
  message: string;
  selection_hash: string;
  version: number;
  status: 'pending' | 'completed';
  is_final: boolean;
  used_fallback: boolean;
  created_at: string;
  completed_at: string | null;
}

export interface PhaseTaskSelectionSummaryEnvelope {
  summary: PhaseTaskSelectionSummary | null;
  latest_requested_version: number | null;
  latest_completed_version: number | null;
}

export interface SavePhaseTaskSelectionSummaryRequest {
  source_entry_key: string;
  selected_task_ids: string[];
  client_change_id: string;
}

export interface TaskSelectionPromptMessage {
  id: string;
  project_id: string;
  session_id: string | null;
  content: string;
  author: string;
  created_at: string;
  phase: string | null;
  allowed_buttons: string[] | null;
  buttons: Array<Record<string, unknown>> | null;
  clicked_button: string | null;
  clicked_buttons: string[] | null;
}

export interface TaskSelectionPromptResponse {
  created: boolean;
  entry_id: string;
  message: TaskSelectionPromptMessage | null;
}

const authHeaders = (includeJson = false): Record<string, string> => {
  const token = tokenService.getAccessToken();
  const headers: Record<string, string> = {};

  if (includeJson) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  return headers;
};

export const buildPhaseService = {
  ensureTaskSelectionPrompt: async (
    projectId: string,
    phaseId: string,
    requestId: string,
    sessionId?: string | null,
  ): Promise<TaskSelectionPromptResponse> => {
    const response = await axiosInstance.post<TaskSelectionPromptResponse>(
      `/api/v1/projects/${projectId}/build-phases/${phaseId}/task-selection-prompt`,
      { request_id: requestId, session_id: sessionId ?? null },
    );

    return response.data;
  },

  getTaskSelectionSummary: async (
    projectId: string,
    phaseId: string,
  ): Promise<PhaseTaskSelectionSummaryEnvelope> => {
    const response = await axiosInstance.get<PhaseTaskSelectionSummaryEnvelope>(
      `/api/v1/projects/${projectId}/build-phases/${phaseId}/task-selection-summary`,
    );

    return response.data;
  },

  saveTaskSelectionSummary: async (
    projectId: string,
    phaseId: string,
    request: SavePhaseTaskSelectionSummaryRequest,
  ): Promise<PhaseTaskSelectionSummaryEnvelope> => {
    const response = await axiosInstance.post<PhaseTaskSelectionSummaryEnvelope>(
      `/api/v1/projects/${projectId}/build-phases/${phaseId}/task-selection-summary`,
      request,
    );

    return response.data;
  },

  getBuildPhases: async (projectId: string): Promise<BuildPhase[]> => {
    const response = await fetch(`${API_URL}/api/v1/projects/${projectId}/build-phases`, {
      headers: authHeaders(),
    });

    if (!response.ok) {
      throw await responseError(response);
    }

    return response.json();
  },

  getExecutionState: async (
    projectId: string,
    modelTier = 'super_large',
  ): Promise<PhaseBuildExecutionState> => {
    const response = await axiosInstance.get<PhaseBuildExecutionState>(
      `/api/v1/projects/${projectId}/build-phases/execution-state`,
      { params: { model_tier: modelTier } },
    );

    return response.data;
  },

  getBuildReadiness: async (projectId: string): Promise<BuildReadiness> => {
    const response = await axiosInstance.get<BuildReadiness>(`/api/v1/projects/${projectId}/build-phases/readiness`);

    return response.data;
  },

  autoArrangePhases: async (projectId: string): Promise<BuildPhase[]> => {
    const response = await axiosInstance.post<BuildPhase[]>(
      `/api/v1/projects/${projectId}/build-phases/auto-arrange`,
    );

    return response.data;
  },

  createBuildPhase: async (
    projectId: string,
    phase: {
      name: string;
      goal?: string | null;
      description?: string | null;
      deliverables?: string[];
      personas?: string[];
      pages?: string[];
    }
  ): Promise<BuildPhase> => {
    const response = await fetch(`${API_URL}/api/v1/projects/${projectId}/build-phases`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify(phase),
    });

    if (!response.ok) {
      throw await responseError(response);
    }

    return response.json();
  },

  moveTask: async (
    projectId: string,
    taskId: string,
    phaseId: string | null
  ): Promise<MoveTaskToPhaseResponse> => {
    const response = await fetch(`${API_URL}/api/v1/projects/${projectId}/build-phases/move-task`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({
        task_id: taskId,
        phase_id: phaseId,
      }),
    });

    if (!response.ok) {
      throw await responseError(response);
    }

    return response.json();
  },

  subscribeToUpdates: (
    projectId: string,
    callback: (phases: BuildPhase[]) => void
  ): (() => void) => {
    eventService.connect(projectId);

    return eventService.subscribe('build_phases_update', (data: unknown) => {
      callback(data as BuildPhase[]);
    });
  },
};
